import { requestJSON } from '@/lib/api/api';
import type { GlobalSearchResult } from '@/lib/api/apiTypes';
import { useUserOriginCoords } from '@/lib/location/locationOrigin';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';

const searchKeys = {
  all: ['global-search'] as const,
  query: (query: string, originLatitude: number, originLongitude: number) =>
    [...searchKeys.all, query, originLatitude, originLongitude] as const,
};

function getSearchAPIQuery(query: string) {
  // Keep @ in the UI query for people intent, but strip it before hitting the server search endpoint.
  return query.trim().replace(/^@+/, '');
}

export function useSearch(query: string, enabled: boolean, limit = 8) {
  const origin = useUserOriginCoords();
  const trimmedQuery = query.trim();
  const [debouncedQuery, setDebouncedQuery] = React.useState(trimmedQuery);
  const apiQuery = getSearchAPIQuery(debouncedQuery);

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedQuery(trimmedQuery);
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [trimmedQuery]);

  return useQuery({
    queryKey: searchKeys.query(apiQuery, origin.latitude, origin.longitude),
    enabled: enabled && apiQuery.length >= 2,
    queryFn: () => {
      const params = new URLSearchParams({
        q: apiQuery,
        limit: String(limit),
        lat: String(origin.latitude),
        lng: String(origin.longitude),
        origin_lat: String(origin.latitude),
        origin_lng: String(origin.longitude),
      });

      return requestJSON<GlobalSearchResult>(`/search?${params.toString()}`, {
        analyticsAction: 'load_search',
      });
    },
    staleTime: 30_000,
  });
}
